import React, { useState } from 'react';
import { Calendar as CalendarIcon } from 'lucide-react';
import { format } from 'date-fns';
import { Button } from './ui/button';
import { Calendar } from './ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';

/**
 * Compact calendar picker used to backdate documents.
 *
 * Props
 * - value: string ISO date or null (e.g. '2026-05-15T11:00:00+00:00')
 * - onChange: (yyyyMmDd) => void  — fires with 'YYYY-MM-DD' on select
 * - disabled?: boolean
 * - label?: string (visible button label, default = formatted value)
 * - testId?: string base testid (button gets `${testId}-trigger`, calendar gets `${testId}-calendar`)
 */
export default function DatePickerButton({
  value,
  onChange,
  disabled = false,
  label,
  testId = 'date-picker',
}) {
  const [open, setOpen] = useState(false);
  const parsed = value ? new Date(value) : null;
  const display = label || (parsed && !isNaN(parsed) ? format(parsed, 'PPP') : 'Pick a date');

  const handleSelect = (d) => {
    if (!d) return;
    // Format as YYYY-MM-DD (local), avoiding timezone shifts that
    // `toISOString()` would introduce by converting to UTC.
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    onChange?.(`${y}-${m}-${day}`);
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          disabled={disabled}
          className="border-[#E6EBE8] text-[#1A1D1C] hover:bg-[#F5F6F5] text-sm font-medium"
          data-testid={`${testId}-trigger`}
        >
          <CalendarIcon className="w-4 h-4 mr-2 text-[#6B7A73]" />
          {display}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        className="w-auto p-0 bg-white border-[#E6EBE8]"
        data-testid={`${testId}-calendar`}
      >
        <Calendar
          mode="single"
          selected={parsed && !isNaN(parsed) ? parsed : undefined}
          onSelect={handleSelect}
          initialFocus
        />
      </PopoverContent>
    </Popover>
  );
}
